/**
 * Site público do Makila, no domínio principal (makila.sistemamz.com):
 * apresentação e venda, preços, ajuda, suporte e a porta de entrada para a
 * aplicação de cada empresa (<empresa>.makila.sistemamz.com).
 *
 * Identidade: img/logo (vermelho #A6070C, grafite #3A3A3B, preto #111, Poppins).
 * O motivo gráfico é o recibo do símbolo — a borda serrilhada (zigzag-b).
 * Sem imagens externas: o CSP só permite 'self'; ilustrações em HTML/SVG.
 */
import {
  ArrowRight, BadgePercent, BarChart3, Boxes, Check, Clock, FileCheck2, FileText, Fingerprint, Handshake, Landmark, LifeBuoy, Lock, LogIn, Mail, MapPin,
  Menu, MessageCircle, Phone, QrCode, Receipt, ScrollText, Share2, ShieldCheck, Smartphone, Users, Wallet, X,
} from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router';
import { MakilaLogo, MakilaMark } from '../components/brand';
import { Alert, Button, Field, Input, Loading, Textarea } from '../components/ui';
import { cn } from '../lib/cn';
import { api, get, post } from '../lib/api';
import { getBaseHost, getWebConfig } from '../lib/tenant-host';
import { Accordion, captureRefParam, ReferralBanner } from './partners';
import { waMe } from '@makila/shared';

/** Contactos do produtor (Ribeiro Tecnologias e Serviços, Lda.). Um só sítio. */
export const CONTACT = {
  company: 'Ribeiro Tecnologias e Serviços, Lda.',
  phone: '+258 86 412 3111',
  whatsapp: '258864123111',
  email: 'info@ribeiro.co.mz',
  city: 'Bairro Matundo, Cidade de Tete',
};
/** Conversa com a equipa (número de CONTACT), com texto pronto. */
export const waLink = (text: string) => waMe(CONTACT.phone, text) ?? `https://wa.me/${CONTACT.whatsapp}`;

const NAV = [
  { to: '/#funcionalidades', label: 'Funcionalidades' },
  { to: '/precos', label: 'Preços' },
  { to: '/ajuda', label: 'Como fazer?' },
  { to: '/suporte', label: 'Suporte' },
  { to: '/parceiros', label: 'Parceiros' },
];

export const btn = {
  primary: 'inline-flex items-center justify-center gap-2 rounded-xl bg-brand-700 px-5 h-12 text-[15px] font-semibold text-white shadow-lg shadow-brand-900/20 transition hover:bg-brand-800 active:translate-y-px',
  dark: 'inline-flex items-center justify-center gap-2 rounded-xl bg-[#111] px-5 h-12 text-[15px] font-semibold text-white transition hover:bg-black active:translate-y-px',
  ghost: 'inline-flex items-center justify-center gap-2 rounded-xl px-5 h-12 text-[15px] font-semibold text-[#111] ring-1 ring-inset ring-black/15 transition hover:bg-black/[.04] active:translate-y-px',
  white: 'inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 h-12 text-[15px] font-semibold text-brand-700 shadow-lg transition hover:bg-brand-50 active:translate-y-px',
};

// ═══ Moldura ═══════════════════════════════════════════════════════════

export function PortalLayout() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  // O site é sempre claro (o tema escuro é da aplicação).
  useEffect(() => { document.documentElement.classList.remove('dark'); }, []);
  // Link de parceiro (?ref=CODIGO em qualquer página): guarda-se para o registo.
  useEffect(() => { captureRefParam(loc.search); }, [loc.search]);
  useEffect(() => {
    setOpen(false);
    if (loc.hash) document.getElementById(loc.hash.slice(1))?.scrollIntoView({ behavior: 'smooth' });
    else window.scrollTo(0, 0);
  }, [loc.pathname, loc.hash]);

  return (
    <div className="min-h-full bg-white font-sans text-[#111] [--bg:#fff] [--border:#ececee] [--muted:#5d5d63] [--surface:#fff] [--surface-2:#f6f6f7] [--text:#111]">
      {!loc.pathname.startsWith('/parceiro') && <ReferralBanner key={loc.search} />}
      <header className="sticky top-0 z-30 border-b border-black/[.06] bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-6 px-4 sm:px-6">
          <Link to="/" className="shrink-0" aria-label="Makila — início"><MakilaLogo className="h-11 w-auto" /></Link>
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Principal">
            {NAV.map((n) => <Link key={n.to} to={n.to} className="rounded-lg px-3 py-2 text-[14.5px] font-medium text-[#3A3A3B] transition hover:bg-black/[.04] hover:text-[#111]">{n.label}</Link>)}
          </nav>
          <div className="ml-auto hidden items-center gap-2 sm:flex">
            <Link to="/entrar" className="inline-flex h-10 items-center gap-2 rounded-lg px-3.5 text-[14.5px] font-semibold text-[#111] hover:bg-black/[.04]"><LogIn className="size-4" />Entrar</Link>
            <Link to="/registar" className="inline-flex h-10 items-center rounded-lg bg-brand-700 px-4 text-[14.5px] font-semibold text-white shadow-sm transition hover:bg-brand-800">Experimentar grátis</Link>
          </div>
          <button type="button" className="ml-auto grid size-10 place-items-center rounded-lg hover:bg-black/5 sm:ml-0 lg:hidden" aria-label={open ? 'Fechar menu' : 'Abrir menu'} aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
        {open && (
          <nav className="border-t border-black/[.06] bg-white px-4 pb-5 pt-2 lg:hidden" aria-label="Menu">
            {NAV.map((n) => <Link key={n.to} to={n.to} className="block rounded-lg px-3 py-3 text-base font-medium hover:bg-black/[.04]">{n.label}</Link>)}
            <Link to="/parceiro/entrar" className="block rounded-lg px-3 py-3 text-base font-medium text-brand-700 hover:bg-black/[.04]">Área de parceiro</Link>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Link to="/entrar" className={cn(btn.ghost, 'h-11')}>Entrar</Link>
              <Link to="/registar" className={cn(btn.primary, 'h-11')}>Experimentar</Link>
            </div>
          </nav>
        )}
      </header>
      <main><Outlet /></main>
      <SiteFooter />
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="relative bg-[#111] text-white/70">
      <div aria-hidden className="flex h-1.5"><span className="w-1/4 bg-[#3A3A3B]" /><span className="flex-1 bg-brand-700" /></div>
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1.3fr]">
        <div>
          <MakilaLogo tone="dark" className="h-14 w-auto" />
          <p className="mt-3 max-w-xs text-sm leading-relaxed">Programa de facturação feito em Moçambique, para empresas que vendem em meticais.</p>
        </div>
        <FooterCol title="Produto" links={[['/#funcionalidades', 'Funcionalidades'], ['/precos', 'Preços'], ['/renovacoes', 'Renovações'], ['/registar', 'Criar conta']]} />
        <FooterCol title="Parceiros" links={[['/parceiros', 'Programa de parceiros'], ['/parceiros#candidatura', 'Tornar-se parceiro'], ['/parceiro/entrar', 'Área de parceiro'], ['/parceiros/termos', 'Termos do programa']]} />
        <FooterCol title="Ajuda" links={[['/ajuda', 'Como fazer?'], ['/suporte', 'Suporte'], ['/entrar', 'Entrar na empresa'], ['/recuperar-password', 'Recuperar password']]} />
        <div>
          <h3 className="mb-3 text-sm font-semibold text-white">Fale connosco</h3>
          <ul className="space-y-2.5 text-sm">
            <li><a href={waLink('Olá, tenho uma pergunta sobre o Makila.')} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white"><MessageCircle className="size-4 text-brand-400" />WhatsApp {CONTACT.phone}</a></li>
            <li><a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 hover:text-white"><Mail className="size-4 text-brand-400" />{CONTACT.email}</a></li>
            <li className="flex items-start gap-2"><MapPin className="mt-0.5 size-4 shrink-0 text-brand-400" />{CONTACT.city}, Moçambique</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h3 className="text-sm font-semibold text-white">Formas de pagamento</h3>
            <p className="mt-0.5 text-xs text-white/45">Pague a subscrição pelo telemóvel ou por transferência bancária.</p>
          </div>
          <ul className="flex flex-wrap items-center gap-2.5" aria-label="Formas de pagamento aceites">
            {PAY_LOGOS.map((l) => (
              <li key={l.src} className="grid h-12 w-[80px] place-items-center rounded-lg bg-white px-2 shadow-sm shadow-black/40 ring-1 ring-white/10">
                <img src={l.src} alt={l.name} title={l.name} loading="lazy" className="max-h-9 w-auto" />
              </li>
            ))}
            <li className="flex h-12 items-center gap-2 rounded-lg border border-white/15 px-3 text-xs font-medium text-white/80" title="Transferência bancária">
              <Landmark className="size-4 text-brand-400" />Transferência bancária
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-5 text-xs text-white/45 sm:px-6">
          <span>© {new Date().getFullYear()} {CONTACT.company} · NUIT 401667040</span>
          <span className="flex flex-wrap gap-x-4 gap-y-1"><Link to="/termos" className="hover:text-white">Termos de Serviço</Link><Link to="/privacidade" className="hover:text-white">Política de Privacidade</Link><span>Makila é uma marca da Ribeiro Tecnologias e Serviços</span></span>
        </div>
      </div>
    </footer>
  );
}

const PAY_LOGOS = [
  { src: '/pagamentos/mpesa.svg', name: 'M-Pesa' },
  { src: '/pagamentos/emola.svg', name: 'e-Mola' },
  { src: '/pagamentos/mkesh.svg', name: 'mKesh' },
];

function FooterCol({ title, links }: { title: string; links: [string, string][] }) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-semibold text-white">{title}</h3>
      <ul className="space-y-2.5 text-sm">{links.map(([to, l]) => <li key={to}><Link to={to} className="hover:text-white">{l}</Link></li>)}</ul>
    </div>
  );
}

// ═══ Peças gráficas ════════════════════════════════════════════════════

/** QR decorativo (não codifica nada) — o desenho de um QR com os três olhos. */
function FakeQr({ className }: { className?: string }) {
  const n = 21;
  const eye = (x: number, y: number) => x < 7 && y < 7 || x >= n - 7 && y < 7 || x < 7 && y >= n - 7;
  const cells: ReactNode[] = [];
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    if (eye(x, y)) continue;
    if ((x * 7 + y * 13 + x * y * 3) % 5 < 2) cells.push(<rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" />);
  }
  const E = ({ x, y }: { x: number; y: number }) => <g><rect x={x} y={y} width="7" height="7" /><rect x={x + 1} y={y + 1} width="5" height="5" fill="#fff" /><rect x={x + 2} y={y + 2} width="3" height="3" /></g>;
  return (
    <svg viewBox={`-1 -1 ${n + 2} ${n + 2}`} className={className} aria-hidden shapeRendering="crispEdges">
      <rect x="-1" y="-1" width={n + 2} height={n + 2} fill="#fff" />
      <g fill="#111">{cells}<E x={0} y={0} /><E x={n - 7} y={0} /><E x={0} y={n - 7} /></g>
    </svg>
  );
}

const HERO_LINES = [
  { d: 'Cimento Portland 42,5 — saco 50 kg', q: '40', p: '520,00', t: '20 800,00' },
  { d: 'Varão de aço nervurado 12 mm', q: '25', p: '690,00', t: '17 250,00' },
  { d: 'Tinta plástica branca 20 L', q: '3', p: '3 450,00', t: '10 350,00' },
];

/** A factura do herói: um recibo com a borda do logótipo. Dados fictícios. */
function HeroInvoice() {
  return (
    <div className="relative mx-auto w-full max-w-[460px]">
      <div className="absolute -inset-6 -z-10 rounded-[2.5rem] bg-gradient-to-br from-brand-700/15 via-transparent to-[#3A3A3B]/10 blur-2xl" />
      <div className="zigzag-b rotate-[1.2deg] rounded-t-2xl bg-white p-4 shadow-[0_30px_60px_-20px_rgb(17_17_17/.35),0_0_0_1px_rgb(17_17_17/.06)] min-[420px]:p-6 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-[15px] font-bold">Ferragens Zambeze, Lda</div>
            <div className="text-[11px] text-[#5d5d63]">NUIT 400 123 456 · Av. Eduardo Mondlane, Tete</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold tracking-[.18em] text-brand-700">FACTURA</div>
            <div className="font-display text-lg font-bold whitespace-nowrap tabular-nums">FT 2026/148</div>
          </div>
        </div>
        <div className="mt-4 rounded-lg bg-[#f6f6f7] px-3 py-2 text-[11px]">
          <span className="text-[#5d5d63]">Cliente</span> <b className="ml-1">Construções Chingodzi, SA</b> <span className="text-[#5d5d63]">· NUIT 401 987 654</span>
        </div>
        <table className="mt-4 w-full text-[11.5px]">
          <thead><tr className="border-b border-black/10 text-left text-[10px] uppercase tracking-wider text-[#5d5d63]"><th className="pb-1.5 font-medium">Descrição</th><th className="pb-1.5 text-right font-medium">Qtd</th><th className="pb-1.5 text-right font-medium">Total</th></tr></thead>
          <tbody>{HERO_LINES.map((l) => (
            <tr key={l.d} className="border-b border-dashed border-black/10"><td className="py-2 pr-2">{l.d}<div className="text-[10px] text-[#5d5d63]">{l.p} MT · IVA 16%</div></td><td className="py-2 text-right tabular-nums">{l.q}</td><td className="py-2 text-right tabular-nums">{l.t}</td></tr>
          ))}</tbody>
        </table>
        <div className="mt-4 flex items-end justify-between gap-4">
          <div className="flex items-end gap-3">
            <FakeQr className="size-[74px] rounded-md ring-1 ring-black/10" />
            <div className="hidden pb-1 text-[9.5px] leading-snug text-[#5d5d63] min-[420px]:block"><span className="font-mono text-[#111]">aB3x</span> — Processado por<br />programa informático<br /><span className="text-brand-700">Verificar: /v/K7Q2M</span></div>
          </div>
          <dl className="min-w-[140px] space-y-1 text-[11.5px]">
            <div className="flex justify-between gap-3"><dt className="text-[#5d5d63]">Base</dt><dd className="tabular-nums">48 400,00</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-[#5d5d63]">IVA 16%</dt><dd className="tabular-nums">7 744,00</dd></div>
            <div className="flex justify-between gap-3 border-t border-black/10 pt-1.5 font-display text-[15px] font-bold"><dt>Total</dt><dd className="tabular-nums">56 144,00 MT</dd></div>
          </dl>
        </div>
      </div>
      {/* Notas a flutuar: o que acontece à volta da factura */}
      <div className="animate-float absolute -bottom-14 -left-2 hidden rounded-xl bg-white px-3.5 py-2.5 shadow-xl ring-1 ring-black/5 sm:-left-10 sm:block">
        <div className="flex items-center gap-2.5">
          <span className="grid size-8 place-items-center rounded-lg bg-emerald-500/12 text-emerald-700"><Wallet className="size-4" /></span>
          <div><div className="text-[11px] text-[#5d5d63]">Recebido por M-Pesa</div><div className="text-sm font-semibold tabular-nums">56 144,00 MT</div></div>
        </div>
      </div>
      <div className="animate-float absolute -right-2 -top-12 hidden rounded-xl bg-[#111] px-3.5 py-2.5 text-white shadow-xl [animation-delay:-3s] sm:-right-8 sm:block">
        <div className="flex items-center gap-2.5">
          <span className="grid size-8 place-items-center rounded-lg bg-white/10"><Landmark className="size-4" /></span>
          <div><div className="text-[11px] text-white/60">Ficheiro AT · Setembro</div><div className="text-sm font-semibold">Pronto a submeter</div></div>
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, children, center, dark }: { eyebrow: string; title: ReactNode; children?: ReactNode; center?: boolean; dark?: boolean }) {
  return (
    <div className={cn('mb-12 max-w-2xl', center && 'mx-auto text-center')}>
      <div className={cn('mb-3 text-[13px] font-semibold uppercase tracking-[.16em]', dark ? 'text-brand-300' : 'text-brand-700')}>{eyebrow}</div>
      <h2 className={cn('font-display text-3xl font-bold leading-[1.15] sm:text-[40px]', dark && 'text-white')}>{title}</h2>
      {children && <p className={cn('mt-4 text-lg leading-relaxed', dark ? 'text-white/65' : 'text-[#5d5d63]')}>{children}</p>}
    </div>
  );
}

// ═══ Página inicial ════════════════════════════════════════════════════

const SECTORS = ['Lojas e armazéns', 'Ferragens', 'Oficinas', 'Consultoria', 'Distribuição', 'Escolas e formação', 'Clínicas', 'Restauração', 'Transportes', 'Prestação de serviços'];

export function PortalHome() {
  const [cert, setCert] = useState<string | null | undefined>(undefined);
  useEffect(() => { getWebConfig().then((c) => setCert(c.fiscalCert)).catch(() => setCert(null)); }, []);

  return (
    <>
      {/* ── Herói ───────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        <div aria-hidden className="bg-dots absolute inset-0 [mask-image:radial-gradient(ellipse_at_70%_40%,#000_10%,transparent_70%)]" />
        <div aria-hidden className="absolute -right-40 -top-40 size-[520px] rounded-full bg-brand-700/[.06] blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-14 px-4 pb-20 pt-12 sm:px-6 lg:grid-cols-[1.08fr_1fr] lg:pb-28 lg:pt-20">
          <div className="animate-rise min-w-0">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#111] py-1 pl-1 pr-3 text-[13px] font-medium text-white">
              <span className="rounded-full bg-brand-700 px-2 py-0.5 text-[11px] font-semibold">Novo</span> Facturação feita em Moçambique
            </span>
            <h1 className="mt-6 font-display text-[34px] font-extrabold leading-[1.06] tracking-tight min-[400px]:text-[40px] sm:text-[56px]">
              Facture certo.<br />Receba a tempo.<br /><span className="relative text-brand-700 sm:whitespace-nowrap">Durma descansado.<svg aria-hidden viewBox="0 0 300 12" className="absolute -bottom-2 left-0 w-full" preserveAspectRatio="none"><path d="M2 9C60 3 140 2 298 6" stroke="currentColor" strokeWidth="4" fill="none" strokeLinecap="round" opacity=".35" /></svg></span>
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-[#3A3A3B]">
              O Makila numera, assina e guarda cada factura como a lei manda. Sabe sempre quem lhe deve,
              e no fim do mês o ficheiro para o e-Declaração sai com um clique — sem copiar nada à mão.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/registar" className={btn.primary}>Experimentar 14 dias grátis <ArrowRight className="size-4" /></Link>
              <Link to="/#como-funciona" className={btn.ghost}>Ver como funciona</Link>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-[#5d5d63]"><Check className="size-4 text-emerald-600" />Sem cartão. Sem instalar nada. Cancela quando quiser.</p>
          </div>
          <div className="animate-rise min-w-0 [animation-delay:.15s]"><HeroInvoice /></div>
        </div>
      </section>

      {/* ── Sectores ────────────────────────────────────────────── */}
      <section className="border-y border-black/[.06] bg-[#fafafa]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-2 gap-y-2 px-4 py-6 sm:px-6">
          <span className="mr-2 text-sm font-medium text-[#5d5d63]">Para quem vende em meticais:</span>
          {SECTORS.map((s) => <span key={s} className="rounded-full bg-white px-3 py-1 text-[13px] font-medium text-[#3A3A3B] ring-1 ring-black/[.07]">{s}</span>)}
        </div>
      </section>

      {/* ── O problema ──────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 py-24 sm:px-6">
        <SectionHead eyebrow="Conhece isto?" title={<>Factura no Word, recibo no livro,<br className="hidden sm:block" /> contas no caderno.</>}>
          Funciona — até ao dia em que um número se repete, um cliente jura que já pagou, ou o contabilista liga a pedir o ficheiro da AT para amanhã.
        </SectionHead>
        <div className="grid gap-5 md:grid-cols-3">
          {[
            { before: 'O número salta, repete-se, ou alguém apaga uma factura que não devia.', after: 'O número é dado pelo sistema no momento da emissão. Não salta, não se repete, e uma factura emitida não se apaga.' },
            { before: 'Ninguém sabe ao certo quem deve, quanto, e desde quando.', after: 'Cada factura entra na conta corrente do cliente. Vê os atrasos por antiguidade e manda o extracto em dois cliques.' },
            { before: 'No fim do mês é copiar factura a factura para a folha da AT.', after: 'O ficheiro sai pronto em CSV, Excel ou XML. E o sistema avisa-o quando o prazo se aproxima.' },
          ].map((c, i) => (
            <div key={i} className="group relative overflow-hidden rounded-2xl border border-black/[.07] bg-white">
              <div className="border-b border-dashed border-black/10 bg-[#fafafa] p-6">
                <div className="mb-2 text-[12px] font-semibold uppercase tracking-wider text-[#5d5d63]">Antes</div>
                <p className="text-[15.5px] leading-relaxed text-[#3A3A3B] line-through decoration-brand-700/40 decoration-2">{c.before}</p>
              </div>
              <div className="p-6">
                <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wider text-brand-700"><MakilaMark simple className="size-4" />Com o Makila</div>
                <p className="text-[15.5px] leading-relaxed">{c.after}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Funcionalidades ─────────────────────────────────────── */}
      <section id="funcionalidades" className="scroll-mt-20 bg-[#f6f6f7] py-24">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <SectionHead eyebrow="Funcionalidades" title="Tudo o que precisa para facturar. Nada que atrapalhe.">
            Começa simples e cresce consigo: hoje só facturas e recibos, amanhã stock, compras e uma equipa de vendedores.
          </SectionHead>
          <div className="grid gap-4 md:grid-cols-6">
            <Feature className="md:col-span-4" icon={<FileText />} title="Todos os documentos de que a lei fala">
              Facturas, facturas-recibo, vendas a dinheiro, pró-formas, notas de crédito e de débito, recibos e guias de remessa.
              IVA a 16%, 5% ou isento com o motivo certo impresso; preço com IVA incluído para o retalho; retenção na fonte para serviços.
              <div className="mt-5 flex flex-wrap gap-1.5">{['FT', 'FR', 'VD', 'PF', 'NC', 'ND', 'RE', 'GR', 'GD'].map((t) => <span key={t} className="rounded-md bg-[#111] px-2 py-1 font-mono text-[12px] font-semibold text-white">{t}</span>)}</div>
            </Feature>
            <Feature className="md:col-span-2" icon={<QrCode />} title="QR em cada documento" dark>
              O cliente, o banco ou o fiscal apontam o telemóvel e confirmam que a factura existe e que os valores batem.
            </Feature>
            <Feature className="md:col-span-2" icon={<Wallet />} title="M-Pesa, e-Mola, banco">
              Registe cada pagamento com o meio e a referência da transacção. Os recibos liquidam várias facturas de uma vez, no todo ou em parte.
            </Feature>
            <Feature className="md:col-span-2" icon={<Receipt />} title="Contas correntes">
              Saldos, antiguidade das dívidas, extractos em PDF e limite de crédito por cliente — o sistema avisa antes de vender a quem já deve de mais.
            </Feature>
            <Feature className="md:col-span-2" icon={<Landmark />} title="Ficheiro da AT e SAF-T">
              Comunicação mensal para o e-Declaração (Aviso 40/AT/DGI/2025) em CSV, Excel ou XML, e SAF-T (MZ) para o contabilista.
            </Feature>
            <Feature className="md:col-span-3" icon={<Boxes />} title="Stock e compras">
              Saída automática na venda, entrada na compra, custo médio ponderado, vários armazéns, alertas de stock mínimo e inventários que acertam tudo no fecho.
            </Feature>
            <Feature className="md:col-span-3" icon={<Users />} title="A equipa, cada um no seu lugar">
              Vendedor factura e recebe; contabilista vê relatórios e exporta; consulta só olha. Dupla verificação no login para quem quiser.
            </Feature>
            <Feature className="md:col-span-2" icon={<BarChart3 />} title="Relatórios">
              Vendas por período, cliente e artigo; mapa de IVA; recebimentos por meio de pagamento.
            </Feature>
            <Feature className="md:col-span-2" icon={<Smartphone />} title="No telemóvel também">
              Corre no browser do computador, do tablet ou do telemóvel. Não há nada para instalar nem actualizar.
            </Feature>
            <Feature className="md:col-span-2" icon={<Mail />} title="Envio ao cliente">
              A factura segue por email com o PDF anexado, e fica registado quando e para quem foi.
            </Feature>
          </div>
        </div>
      </section>

      {/* ── Como funciona ───────────────────────────────────────── */}
      <section id="como-funciona" className="scroll-mt-20 mx-auto max-w-6xl px-4 py-24 sm:px-6">
        <SectionHead eyebrow="Como funciona" title="Da conta criada à primeira factura, numa manhã." center />
        <ol className="relative grid gap-10 md:grid-cols-3 md:gap-6">
          <div aria-hidden className="absolute left-0 right-0 top-7 hidden border-t-2 border-dashed border-black/10 md:block" />
          {[
            { t: '2 minutos', h: 'Crie a conta', p: 'Firma, NUIT e o endereço da empresa — algo como sua-empresa.makila.sistemamz.com. Sem cartão.' },
            { t: '30 minutos', h: 'Arrume a casa', p: 'Logótipo, contas bancárias, artigos e clientes. Treine a equipa no modo formação, onde nada conta para a AT.' },
            { t: 'Todos os dias', h: 'Facture a sério', p: 'Active a emissão fiscal e pronto: documentos numerados, assinados e com QR, prontos a imprimir ou enviar.' },
          ].map((s, i) => (
            <li key={s.h} className="relative">
              <div className="relative mb-5 grid size-14 place-items-center rounded-2xl bg-brand-700 font-display text-xl font-bold text-white shadow-lg shadow-brand-900/20">{i + 1}</div>
              <div className="mb-1 flex items-center gap-1.5 text-[13px] font-medium text-[#5d5d63]"><Clock className="size-3.5" />{s.t}</div>
              <h3 className="font-display text-xl font-bold">{s.h}</h3>
              <p className="mt-2 leading-relaxed text-[#3A3A3B]">{s.p}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Confiança: o que não se vê ──────────────────────────── */}
      <section className="relative overflow-hidden bg-[#111] py-24 text-white">
        <svg aria-hidden className="pointer-events-none absolute -right-10 top-16 hidden h-[460px] rotate-[8deg] opacity-[.09] lg:block" viewBox="120 80 272 362" fill="none" stroke="#fff" strokeWidth="2"><path d="M132 120Q132 92 160 92L352 92Q380 92 380 120L380 404L359.3 430L338.7 404L318 430L297.3 404L276.7 430L256 404L235.3 430L214.7 404L194 430L173.3 404L152.7 430L132 404Z" /><path d="M178 300L178 160L238 236L334 132L334 300" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" /><path d="M178 343H334M178 371H274M292 371H334" strokeWidth="6" strokeLinecap="round" /></svg>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <SectionHead dark eyebrow="Por dentro" title="O que não se vê é o que mais conta.">
            Uma factura é um documento legal. Tratamo-la como tal — e não só no ecrã: as regras estão gravadas na própria base de dados.
          </SectionHead>
          <div className="grid gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { i: <Lock />, h: 'Emitido é definitivo', p: 'Nem a aplicação nem um erro de programação conseguem alterar ou apagar um documento emitido. Corrige-se com nota de crédito, como manda o CIVA.' },
              { i: <Fingerprint />, h: 'Assinatura encadeada', p: 'Cada documento é assinado (RSA-SHA256) e ligado ao anterior da mesma série. Todos os dias o sistema verifica a cadeia inteira.' },
              { i: <ScrollText />, h: 'Auditoria que não se apaga', p: 'Quem emitiu, quem alterou, a que horas e de onde. O registo só aceita entradas novas.' },
              { i: <ShieldCheck />, h: 'Cada empresa no seu cofre', p: 'Os dados de uma empresa são invisíveis às outras, por regra da base de dados e não apenas do programa.' },
              { i: <FileCheck2 />, h: 'Guardado 10 anos', p: 'A lei pede cinco. Mesmo que deixe de pagar, a conta fica em consulta e pode exportar tudo — nunca apagamos documentos fiscais.' },
              { i: <MapPin />, h: 'Feito cá, suportado cá', p: 'Desenvolvido pela Ribeiro Tecnologias e Serviços, em Tete. Quando ligar, fala com quem conhece o sistema e a lei moçambicana.' },
            ].map((x) => (
              <div key={x.h} className="bg-[#111] p-7">
                <div className="mb-4 inline-flex rounded-xl bg-brand-700/20 p-2.5 text-brand-300 [&>svg]:size-5">{x.i}</div>
                <h3 className="font-display text-lg font-semibold">{x.h}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-white/60">{x.p}</p>
              </div>
            ))}
          </div>
          {cert !== undefined && (
            <div className="mt-8 flex items-start gap-3 rounded-xl border border-white/10 bg-white/[.03] p-5 text-sm text-white/70">
              <Landmark className="mt-0.5 size-5 shrink-0 text-brand-300" />
              {cert
                ? <p><b className="text-white">Programa autorizado pela Autoridade Tributária, n.º {cert}/AT.</b> Cada documento leva esta menção impressa.</p>
                : <p><b className="text-white">Sobre a autorização da AT:</b> a emissão de documentos com valor fiscal fica disponível assim que o programa tiver a autorização da Autoridade Tributária (Despacho de 22/12/2011). Até lá, pode usar o Makila em modo formação — preparar artigos e clientes, treinar a equipa e ver as facturas exactamente como vão sair.</p>}
            </div>
          )}
        </div>
      </section>

      {/* ── Preços ─────────────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 py-24 sm:px-6">
        <SectionHead eyebrow="Preços" title="Paga pelo tamanho do negócio, não por funcionalidade escondida." center>
          Todos os planos têm os documentos fiscais, o QR e o ficheiro da AT. Pague o ano e ganhe dois meses.
        </SectionHead>
        <Pricing />
      </section>

      {/* ── Perguntas ──────────────────────────────────────────── */}
      <section className="bg-[#f6f6f7] py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[1fr_1.5fr]">
          <div>
            <SectionHead eyebrow="Perguntas" title="O que nos perguntam antes de começar." />
            <p className="-mt-6 text-[#5d5d63]">Não encontra a sua? <Link to="/suporte" className="font-semibold text-brand-700 hover:underline">Pergunte-nos</Link> ou veja os guias em <Link to="/ajuda" className="font-semibold text-brand-700 hover:underline">Como fazer?</Link></p>
          </div>
          <Faq items={[
            { q: 'Preciso de instalar alguma coisa?', a: 'Não. O Makila corre no browser — Chrome, Edge, Firefox ou Safari — no computador, no tablet ou no telemóvel. As actualizações chegam sozinhas.' },
            { q: 'O que acontece no fim dos 14 dias?', a: 'Escolhe um plano e paga por M-Pesa, e-Mola, cartão ou transferência. Se não escolher, a conta fica em consulta: vê e exporta tudo, só não emite documentos novos. Nada é apagado.' },
            { q: 'Já facturo noutro programa. Posso mudar a meio do ano?', a: 'Pode. No Makila começa com séries novas; as facturas antigas continuam válidas no programa anterior, que fica como arquivo. Se tiver muitos clientes e artigos para passar, fale connosco e ajudamos.' },
            { q: 'Os meus dados estão seguros?', a: 'A ligação é cifrada (HTTPS), cada empresa só vê os seus dados, e há cópias de segurança. Pode activar a dupla verificação no login. Os documentos fiscais guardam-se pelo menos 10 anos.' },
            { q: 'Funciona sem internet?', a: 'Precisa de ligação para emitir, porque é aí que o documento é numerado e assinado. Basta pouca: os dados do telemóvel chegam bem.' },
            { q: 'O meu contabilista pode ter acesso?', a: 'Sim. Convide-o com o papel Contabilista: vê os documentos, os relatórios e gera os ficheiros da AT e o SAF-T, sem poder mexer nas definições.' },
          ]} />
        </div>
      </section>

      {/* ── Programa de parceiros ───────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-4 py-24 sm:px-6">
        <div className="grid items-center gap-10 overflow-hidden rounded-3xl bg-[#111] p-8 text-white sm:p-12 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <div className="text-[13px] font-semibold uppercase tracking-[.16em] text-brand-300">Programa de parceiros</div>
            <h2 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-[40px]">Recomende o Makila e ganhe 20% durante um ano.</h2>
            <p className="mt-4 max-w-xl text-lg text-white/70">Contabilistas, revendedores de TI, consultores e clientes: por cada empresa que trouxer, recebe 20% do que ela pagar nos primeiros 12 meses. Não precisa de ser assinante.</p>
            <div className="mt-8 flex flex-col gap-3 whitespace-nowrap sm:flex-row sm:flex-wrap">
              <Link to="/parceiros#candidatura" className={btn.white}>Tornar-se parceiro <ArrowRight className="size-4" /></Link>
              <Link to="/parceiros" className="inline-flex h-12 items-center justify-center gap-2 rounded-xl px-5 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/30 hover:bg-white/10">Como funciona</Link>
              <Link to="/parceiro/entrar" className="inline-flex h-12 items-center justify-center gap-2 rounded-xl px-5 text-[15px] font-semibold text-white/80 hover:text-white"><LogIn className="size-4" />Área de parceiro</Link>
            </div>
          </div>
          <ul className="space-y-4">
            {[
              [<Share2 key="s" />, 'Link, código e QR só seus', 'Mensagens prontas para WhatsApp, email e redes, e um folheto para imprimir.'],
              [<BadgePercent key="b" />, '10% para quem indicar', 'A empresa ganha desconto no primeiro pagamento — fica mais fácil dizer que sim.'],
              [<Wallet key="w" />, 'Recebe por M-Pesa ou banco', 'Acompanhe empresas e comissões na área de parceiro, a qualquer hora.'],
            ].map(([icon, t, d]) => (
              <li key={t as string} className="flex gap-4 rounded-2xl bg-white/[.06] p-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-brand-700 [&>svg]:size-5">{icon}</span>
                <div><div className="font-semibold">{t}</div><div className="text-sm text-white/60">{d}</div></div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Chamada final ──────────────────────────────────────── */}
      <section className="relative bg-brand-700 text-white">
        <div aria-hidden className="zigzag-t absolute inset-x-0 -top-[13px] h-[14px] bg-brand-700" />
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 px-4 py-20 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl font-bold leading-tight sm:text-[40px]">Experimente com os seus artigos e os seus clientes.</h2>
            <p className="mt-3 text-lg text-white/80">Catorze dias, sem cartão. Se não servir para o seu negócio, não paga nada.</p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 whitespace-nowrap sm:flex-row">
            <Link to="/registar" className={btn.white}>Criar conta grátis <ArrowRight className="size-4" /></Link>
            <a href={waLink('Olá, gostava de ver uma demonstração do Makila.')} target="_blank" rel="noopener noreferrer" className="inline-flex h-12 items-center justify-center gap-2 rounded-xl px-5 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/40 hover:bg-white/10"><MessageCircle className="size-4" />Pedir demonstração</a>
          </div>
        </div>
      </section>
    </>
  );
}

function Feature({ icon, title, children, className, dark }: { icon: ReactNode; title: string; children: ReactNode; className?: string; dark?: boolean }) {
  return (
    <div className={cn('rounded-2xl p-7 ring-1', dark ? 'bg-brand-700 text-white ring-brand-800' : 'bg-white ring-black/[.06]', className)}>
      <div className={cn('mb-4 inline-flex rounded-xl p-2.5 [&>svg]:size-5', dark ? 'bg-white/15 text-white' : 'bg-brand-700/[.08] text-brand-700')}>{icon}</div>
      <h3 className="font-display text-lg font-semibold">{title}</h3>
      <div className={cn('mt-2 text-[15px] leading-relaxed', dark ? 'text-white/80' : 'text-[#3A3A3B]')}>{children}</div>
    </div>
  );
}

/** Perguntas frequentes: o mesmo acordeão animado da página de parceiros. */
function Faq({ items }: { items: { q: string; a: ReactNode }[] }) {
  return <Accordion items={items} />;
}

// ═══ Preços ════════════════════════════════════════════════════════════

// "em breve" = previsto no plano mas ainda não construído (docs/04-ROADMAP.md, Fase 2). Tirar a marca quando sair.
const FEATURE_LABELS: Record<string, string> = {
  stock: 'Stock, armazéns e inventários', purchases: 'Compras e fornecedores', multi_branch: 'Vários armazéns',
  whatsapp: 'Envio por WhatsApp (em breve)', mobile_payments: 'Link de pagamento M-Pesa na factura (em breve)',
  portal: 'Portal para os seus clientes (em breve)', api: 'Acesso à API (em breve)',
};
const PLAN_PITCH: Record<string, string> = {
  essencial: 'Para quem está a começar ou passa poucas facturas por mês.',
  empresa: 'Para a loja ou o escritório com equipa, stock e compras.',
  pro: 'Para quem tem volume, várias filiais ou integrações.',
};
interface Plan { code: string; name: string; price_monthly: string; price_yearly: string; max_users: number | null; max_documents_month: number | null; features: Record<string, boolean> }
/** 1450 → "1 450" (o pt-PT não agrupa números de quatro algarismos). */
const money = (v: string) => String(Math.round(Number(v))).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');

function Pricing() {
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [error, setError] = useState('');
  const [yearly, setYearly] = useState(false);
  useEffect(() => { get<{ plans: Plan[] }>('/reference/plans').then((r) => setPlans(r.plans.filter((p) => p.code !== 'trial'))).catch(() => setError('Não foi possível carregar os preços agora. Tente daqui a pouco.')); }, []);
  return (
    <div>
      <div className="mb-10 flex justify-center">
        <div className="inline-flex rounded-xl bg-[#f0f0f1] p-1 text-sm font-semibold" role="radiogroup" aria-label="Período de pagamento">
          {[[false, 'Mensal'], [true, 'Anual · 2 meses grátis']].map(([v, l]) => (
            <button key={String(v)} type="button" role="radio" aria-checked={yearly === v} onClick={() => setYearly(v as boolean)}
              className={cn('rounded-lg px-4 py-2 transition', yearly === v ? 'bg-white text-[#111] shadow-sm' : 'text-[#5d5d63] hover:text-[#111]')}>{l as string}</button>
          ))}
        </div>
      </div>
      {error && <div className="mx-auto max-w-md"><Alert tone="error">{error}</Alert></div>}
      {!plans && !error && <Loading rows={3} />}
      {plans && (
        <div className="grid items-start gap-5 md:grid-cols-3">
          {plans.map((p) => {
            const star = p.code === 'empresa';
            const price = yearly ? Number(p.price_yearly) / 12 : Number(p.price_monthly);
            return (
              <div key={p.code} className={cn('relative rounded-2xl p-7', star ? 'bg-[#111] text-white shadow-2xl shadow-black/20 md:-mt-4 md:pb-10' : 'bg-white ring-1 ring-black/[.08]')}>
                {star && <span className="absolute -top-3 left-7 rounded-full bg-brand-700 px-3 py-1 text-xs font-semibold text-white">O mais escolhido</span>}
                <h3 className="font-display text-xl font-bold">{p.name}</h3>
                <p className={cn('mt-1 min-h-[44px] text-sm', star ? 'text-white/60' : 'text-[#5d5d63]')}>{PLAN_PITCH[p.code] ?? ''}</p>
                <div className="mt-5 flex items-baseline gap-1.5">
                  <span className="font-display text-[42px] font-bold leading-none tabular-nums">{money(String(Math.round(price)))}</span>
                  <span className={star ? 'text-white/60' : 'text-[#5d5d63]'}>MT/mês</span>
                </div>
                <div className={cn('mt-1 h-5 text-sm', star ? 'text-white/50' : 'text-[#5d5d63]')}>{yearly ? `${money(p.price_yearly)} MT por ano` : 'sem IVA'}</div>
                <Link to="/registar" className={cn('mt-6 flex h-11 items-center justify-center rounded-xl text-[15px] font-semibold transition', star ? 'bg-brand-700 text-white hover:bg-brand-600' : 'bg-[#111] text-white hover:bg-black')}>Começar com o {p.name}</Link>
                <ul className="mt-7 space-y-3 text-[14.5px]">
                  <PlanLi dark={star}>{p.max_users ? `Até ${p.max_users} utilizadores` : 'Utilizadores ilimitados'}</PlanLi>
                  <PlanLi dark={star}>{p.max_documents_month ? `${money(String(p.max_documents_month))} documentos por mês` : 'Documentos ilimitados'}</PlanLi>
                  <PlanLi dark={star}>Todos os documentos fiscais, QR e ficheiro AT</PlanLi>
                  <PlanLi dark={star}>Contas correntes e relatórios</PlanLi>
                  {Object.entries(FEATURE_LABELS).filter(([k]) => p.features?.[k]).map(([k, label]) => <PlanLi key={k} dark={star}>{label}</PlanLi>)}
                </ul>
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-10 text-center text-sm text-[#5d5d63]">Preços em meticais, sem IVA. Contabilista com várias empresas clientes? <Link to="/suporte" className="font-semibold text-brand-700 hover:underline">Fale connosco</Link>.</p>
    </div>
  );
}

function PlanLi({ children, dark }: { children: ReactNode; dark?: boolean }) {
  return <li className="flex gap-2.5"><Check className={cn('mt-0.5 size-4 shrink-0', dark ? 'text-brand-300' : 'text-brand-700')} /><span>{children}</span></li>;
}

/** Cabeçalho das páginas interiores. */
export function PageHero({ eyebrow, title, children }: { eyebrow: string; title: ReactNode; children?: ReactNode }) {
  return (
    <section className="relative overflow-hidden border-b border-black/[.06] bg-[#fafafa]">
      <div aria-hidden className="bg-dots absolute inset-0 [mask-image:linear-gradient(to_bottom,#000,transparent)]" />
      <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16">
        <div className="mb-3 text-[13px] font-semibold uppercase tracking-[.16em] text-brand-700">{eyebrow}</div>
        <h1 className="max-w-3xl font-display text-4xl font-bold leading-tight sm:text-5xl">{title}</h1>
        {children && <p className="mt-4 max-w-2xl text-lg text-[#5d5d63]">{children}</p>}
      </div>
    </section>
  );
}
export { PageHero as PortalPageHero };

export function PortalPrices() {
  return (
    <>
      <PageHero eyebrow="Preços" title="Um plano para cada fase do negócio.">Comece pelo teste grátis de 14 dias. Mude de plano quando quiser, sem perder nada.</PageHero>
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6"><Pricing /></section>
    </>
  );
}

export function PortalRenewals() {
  return (
    <>
      <PageHero eyebrow="Renovações" title="Manter a subscrição em dia.">A renovação faz-se dentro da aplicação da sua empresa, em poucos passos.</PageHero>
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.4fr_1fr]">
        <ol className="space-y-6">
          {[
            <>Entre na aplicação da sua empresa (<b>a-sua-empresa</b>.makila.sistemamz.com). Só o <b>Dono</b> da conta vê esta área.</>,
            <>Abra <b>Definições → Subscrição e pagamento</b>. Vê o plano actual e até quando está válido.</>,
            <>Escolha o plano e a duração. No anual paga 10 meses e usa 12.</>,
            <>Pague por M-Pesa, e-Mola, cartão ou transferência bancária. Na transferência, carregue o comprovativo — activamos assim que o valor entrar.</>,
          ].map((s, i) => (
            <li key={i} className="flex gap-4">
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-brand-700 font-display font-bold text-white">{i + 1}</span>
              <p className="pt-1.5 text-[16px] leading-relaxed text-[#3A3A3B]">{s}</p>
            </li>
          ))}
        </ol>
        <aside className="h-fit rounded-2xl bg-[#111] p-7 text-white">
          <h2 className="font-display text-lg font-semibold">E se a subscrição expirar?</h2>
          <p className="mt-2 leading-relaxed text-white/65">A conta passa a só de leitura. Continua a ver, imprimir e exportar tudo; só não emite documentos novos. Renove e volta tudo ao normal, no mesmo instante.</p>
          <Link to="/suporte" className="mt-5 inline-flex items-center gap-2 font-semibold text-brand-300 hover:text-white">Precisa de ajuda com o pagamento? <ArrowRight className="size-4" /></Link>
        </aside>
      </section>
    </>
  );
}

// ═══ Suporte ═══════════════════════════════════════════════════════════

export function PortalSupport() {
  const [f, setF] = useState({ name: '', email: '', company: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setError('');
    try { await post('/public/support', { ...f, company: f.company || undefined }); setSent(true); }
    catch (err) { setError(err instanceof Error ? err.message : 'Não foi possível enviar.'); }
    finally { setBusy(false); }
  }
  return (
    <>
      <PageHero eyebrow="Suporte" title="Estamos do outro lado.">Uma dúvida, um problema, uma sugestão — escreva-nos. Responde-lhe uma pessoa que conhece o sistema.</PageHero>
      <section className="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1fr_1.4fr]">
        <div className="space-y-4">
          <Channel href={waLink('Olá, preciso de ajuda com o Makila.')} icon={<MessageCircle />} title="WhatsApp" sub={CONTACT.phone}>O mais rápido para dúvidas do dia-a-dia.</Channel>
          <Channel href={`mailto:${CONTACT.email}`} icon={<Mail />} title="Email" sub={CONTACT.email}>Para pedidos com anexos ou questões sobre a subscrição.</Channel>
          <Channel href={`tel:${CONTACT.phone.replace(/\s/g, '')}`} icon={<Phone />} title="Telefone" sub={CONTACT.phone}>Para falar directamente com a equipa.</Channel>
          <Link to="/ajuda" className="flex items-center gap-4 rounded-2xl border border-dashed border-black/15 p-5 transition hover:border-brand-700/40 hover:bg-brand-50/40">
            <span className="grid size-11 place-items-center rounded-xl bg-brand-700/[.08] text-brand-700"><LifeBuoy className="size-5" /></span>
            <div><div className="font-semibold">Como fazer?</div><div className="text-sm text-[#5d5d63]">Guias passo a passo, para resolver já.</div></div>
            <ArrowRight className="ml-auto size-4 text-[#5d5d63]" />
          </Link>
        </div>
        <div className="rounded-2xl bg-white p-6 ring-1 ring-black/[.08] sm:p-8">
          {sent ? (
            <div className="py-10 text-center">
              <div className="mx-auto mb-4 grid size-14 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-700"><Check className="size-7" /></div>
              <h2 className="font-display text-2xl font-bold">Mensagem recebida.</h2>
              <p className="mx-auto mt-2 max-w-sm text-[#5d5d63]">Vamos responder para {f.email} assim que possível.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-display text-xl font-bold">Escreva-nos</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nome"><Input required value={f.name} onChange={set('name')} /></Field>
                <Field label="Email"><Input type="email" required value={f.email} onChange={set('email')} /></Field>
              </div>
              <Field label="Empresa" hint="Opcional"><Input value={f.company} onChange={set('company')} placeholder="Nome ou endereço (ex.: ferragens-tete)" /></Field>
              <Field label="Assunto"><Input required value={f.subject} onChange={set('subject')} /></Field>
              <Field label="Mensagem"><Textarea required rows={6} value={f.message} onChange={set('message')} placeholder="Conte-nos o que estava a fazer e o que aconteceu." /></Field>
              {error && <Alert tone="error">{error}</Alert>}
              <Button type="submit" className="w-full" size="lg" loading={busy}>Enviar mensagem</Button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

function Channel({ href, icon, title, sub, children }: { href: string; icon: ReactNode; title: string; sub: string; children: ReactNode }) {
  return (
    <a href={href} target={href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="flex items-start gap-4 rounded-2xl bg-white p-5 ring-1 ring-black/[.08] transition hover:ring-brand-700/30 hover:shadow-md">
      <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-brand-700 text-white [&>svg]:size-5">{icon}</span>
      <div><div className="font-semibold">{title} <span className="ml-1 font-normal text-[#5d5d63]">{sub}</span></div><div className="mt-0.5 text-sm text-[#5d5d63]">{children}</div></div>
    </a>
  );
}

// ═══ Entrar ════════════════════════════════════════════════════════════

export function PortalEntrar() {
  const [slug, setSlug] = useState('');
  const [base, setBase] = useState('makila.sistemamz.com');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { getBaseHost().then(setBase).catch(() => {}); }, []);
  async function submit(e: FormEvent) {
    e.preventDefault(); setError('');
    const s = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (s.length < 2) { setError('Escreva o endereço da sua empresa.'); return; }
    setBusy(true);
    // Confirma que a empresa existe antes de encaminhar.
    try {
      await api('GET', `/public/tenant/${encodeURIComponent(s)}`);
      window.location.href = `${window.location.protocol}//${s}.${base.split(':')[0]}/entrar`;
    } catch { setError('Não encontrámos nenhuma empresa com esse endereço. Confirme no email de boas-vindas.'); setBusy(false); }
  }
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="bg-dots absolute inset-0 [mask-image:radial-gradient(ellipse_at_center,#000_20%,transparent_70%)]" />
      <div className="relative mx-auto max-w-md px-4 py-20 sm:py-28">
        <div className="zigzag-b rounded-t-3xl bg-white p-8 shadow-[0_30px_60px_-25px_rgb(17_17_17/.3),0_0_0_1px_rgb(17_17_17/.06)]">
          <MakilaMark className="mb-5 size-12" />
          <h1 className="font-display text-2xl font-bold">Entrar na sua empresa</h1>
          <p className="mt-1 text-[#5d5d63]">Escreva o endereço que escolheu no registo.</p>
          <form onSubmit={submit} className="mt-6 space-y-4 pb-4">
            <Field label="Endereço da empresa">
              <div className="flex items-center overflow-hidden rounded-lg border border-[var(--border-strong)] bg-white focus-within:border-brand-500 focus-within:ring-3 focus-within:ring-brand-500/15">
                <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="a-sua-empresa" autoFocus spellCheck={false} autoCapitalize="none" aria-label="Endereço da empresa"
                  className="h-11 min-w-0 flex-1 bg-transparent px-3 text-[15px] outline-none" />
                <span className="shrink-0 truncate pr-3 text-sm text-[#5d5d63]">.{base.split(':')[0]}</span>
              </div>
            </Field>
            {error && <Alert tone="error">{error}</Alert>}
            <Button type="submit" className="w-full" size="lg" loading={busy}>Continuar</Button>
          </form>
        </div>
        <p className="mt-8 text-center text-sm text-[#5d5d63]">Ainda não tem conta? <Link to="/registar" className="font-semibold text-brand-700 hover:underline">Experimente 14 dias grátis</Link></p>
        <Link to="/parceiro/entrar" className="mt-6 flex items-center gap-4 rounded-2xl bg-white p-5 ring-1 ring-black/[.08] transition hover:ring-brand-700/30 hover:shadow-md">
          <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-brand-700/[.08] text-brand-700"><Handshake className="size-5" /></span>
          <div><div className="font-semibold">É parceiro Makila?</div><div className="text-sm text-[#5d5d63]">Entre na área de parceiro — mesmo sem ser assinante.</div></div>
          <ArrowRight className="ml-auto size-4 text-[#5d5d63]" />
        </Link>
      </div>
    </section>
  );
}
